import React from 'react';
import type { Primitive, Zeichnung } from './primitive';
import { farbe } from './primitive';

/**
 * Der Bildschirm- und Druckrenderer der Skizzen.
 *
 * Nimmt eine `Zeichnung` (Primitive in Millimetern) und setzt sie als SVG um.
 * Bewusst duenn: hier wird NICHTS entschieden, nur uebersetzt. Jede Aussage
 * der Zeichnung — Reihenfolge, Richtung, Beschriftung, was fehlt — ist
 * bereits in der Primitivenliste getroffen und dort getestet.
 *
 * Das Gegenstueck ist `services/pdf/skizzeNachPdf.ts`. Beide lesen dieselbe
 * Liste; deshalb koennen Bildschirm und Druck nicht auseinanderlaufen.
 *
 * ═══ WARUM DIE VIEWBOX IN MILLIMETERN LAEUFT ══════════════════════════════
 * Die `viewBox` uebernimmt Breite und Hoehe der Zeichnung unveraendert. Damit
 * ist eine Strecke von 20 mm in der Zeichnung auch auf dem Papier 20 mm lang,
 * sobald das SVG mit `width` in Millimetern ausgegeben wird — und am
 * Bildschirm skaliert es verlustfrei auf jede Breite.
 */

interface Props {
    zeichnung: Zeichnung;
    /** Ueberschrift und Fussnoten mit ausgeben. Fuer die Einbettung in ein
     *  Kapitel, das seine eigene Ueberschrift traegt, abschaltbar. */
    mitRahmen?: boolean;
    className?: string;
}

const SCHRIFT = 'Helvetica, Arial, sans-serif';

/** Millimeter → SVG-Schriftgroesse. In der Zeichnung sind Groessen in Punkt
 *  gedacht (wie im PDF); 1 pt entspricht etwa 0,353 mm. */
const pt = (groesse: number): number => groesse * 0.353;

const ankerZu = (anker?: 'links' | 'mitte' | 'rechts'): 'start' | 'middle' | 'end' => {
    if (anker === 'mitte') return 'middle';
    if (anker === 'rechts') return 'end';
    return 'start';
};

const Teil: React.FC<{ t: Primitive; i: number }> = ({ t, i }) => {
    switch (t.art) {
        case 'rechteck':
            return (
                <rect
                    key={i}
                    x={t.x} y={t.y} width={t.b} height={t.h}
                    rx={t.radius ?? 0}
                    fill={farbe(t.fuellung, 'none')}
                    stroke={farbe(t.linie, '#0f172a')}
                    strokeWidth={0.3}
                    strokeDasharray={t.strich === 'gestrichelt' ? '1.6 1.2' : undefined}
                />
            );

        case 'linie':
            return (
                <line
                    key={i}
                    x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
                    stroke={farbe(t.linie, '#0f172a')}
                    strokeWidth={t.staerke ?? 0.3}
                    strokeDasharray={t.strich === 'gestrichelt' ? '1.6 1.2' : undefined}
                />
            );

        case 'pfeil': {
            // Die Spitze wird als Dreieck gezeichnet und nicht als
            // Marker-Definition: Marker erben die Farbe nicht zuverlaessig
            // ueber alle Druckwege, und im PDF gibt es sie ohnehin nicht.
            const dx = t.x2 - t.x1;
            const dy = t.y2 - t.y1;
            const laenge = Math.hypot(dx, dy) || 1;
            const s = t.spitze ?? 3;
            const ex = dx / laenge;
            const ey = dy / laenge;
            // Schaftende liegt vor der Spitze, sonst ragt die Linie heraus.
            const sx = t.x2 - ex * s;
            const sy = t.y2 - ey * s;
            const nx = -ey;
            const ny = ex;
            const punkte = [
                `${t.x2},${t.y2}`,
                `${sx + nx * s * 0.4},${sy + ny * s * 0.4}`,
                `${sx - nx * s * 0.4},${sy - ny * s * 0.4}`,
            ].join(' ');
            const f = farbe(t.linie, '#0f172a');
            return (
                <g key={i}>
                    <line x1={t.x1} y1={t.y1} x2={sx} y2={sy} stroke={f} strokeWidth={t.staerke ?? 0.4} />
                    <polygon points={punkte} fill={f} />
                </g>
            );
        }

        case 'text':
            return (
                <text
                    key={i}
                    x={t.x} y={t.y}
                    fontSize={pt(t.groesse)}
                    fontFamily={SCHRIFT}
                    fontWeight={t.fett ? 700 : 400}
                    fontStyle={t.kursiv ? 'italic' : undefined}
                    textAnchor={ankerZu(t.anker)}
                    fill={farbe(t.farbe, '#0f172a')}
                >
                    {t.text}
                </text>
            );

        case 'masslinie': {
            const f = '#64748b';
            const waagerecht = Math.abs(t.y2 - t.y1) < 0.01;
            const mx = (t.x1 + t.x2) / 2;
            const my = (t.y1 + t.y2) / 2;
            return (
                <g key={i}>
                    <line x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke={f} strokeWidth={0.25} />
                    {/* Begrenzungsstriche */}
                    <line
                        x1={t.x1} y1={t.y1 - (waagerecht ? 1.2 : 0)}
                        x2={t.x1 + (waagerecht ? 0 : 1.2)} y2={t.y1 + (waagerecht ? 1.2 : 0)}
                        stroke={f} strokeWidth={0.25}
                    />
                    <line
                        x1={t.x2} y1={t.y2 - (waagerecht ? 1.2 : 0)}
                        x2={t.x2 + (waagerecht ? 0 : 1.2)} y2={t.y2 + (waagerecht ? 1.2 : 0)}
                        stroke={f} strokeWidth={0.25}
                    />
                    <text
                        x={waagerecht ? mx : mx + 2}
                        y={waagerecht ? my - 1.4 : my}
                        fontSize={pt(6)} fontFamily={SCHRIFT}
                        textAnchor={waagerecht ? 'middle' : 'start'}
                        fill={f}
                    >
                        {t.text}
                    </text>
                </g>
            );
        }

        case 'bild':
            return (
                <image
                    key={i}
                    href={t.quelle}
                    x={t.x} y={t.y} width={t.b} height={t.h}
                    preserveAspectRatio="xMidYMid meet"
                />
            );

        default:
            return null;
    }
};

export const SkizzeSvg: React.FC<Props> = ({ zeichnung, mitRahmen = true, className }) => (
    <figure className={className ?? 'w-full'}>
        {mitRahmen && (
            <figcaption className="mb-1">
                <span className="block text-sm font-bold text-slate-800">{zeichnung.titel}</span>
                {zeichnung.untertitel && (
                    <span className="block text-[11px] text-slate-500">{zeichnung.untertitel}</span>
                )}
            </figcaption>
        )}

        <svg
            viewBox={`0 0 ${zeichnung.breite} ${zeichnung.hoehe}`}
            width="100%"
            role="img"
            aria-label={zeichnung.titel}
            className="bg-white border border-slate-200 rounded-lg"
            style={{ maxHeight: '70vh' }}
        >
            {zeichnung.teile.map((t, i) => <Teil key={i} t={t} i={i} />)}
        </svg>

        {mitRahmen && zeichnung.legende && zeichnung.legende.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {zeichnung.legende.map((l, i) => (
                    <li key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                        <span
                            className="inline-block w-3 h-3 rounded-sm border border-slate-300"
                            style={{ background: farbe(l.farbe) }}
                        />
                        {l.text}
                    </li>
                ))}
            </ul>
        )}

        {mitRahmen && zeichnung.fussnoten && zeichnung.fussnoten.length > 0 && (
            <ul className="mt-2 space-y-0.5">
                {zeichnung.fussnoten.map((f, i) => (
                    <li key={i} className="text-[10px] text-slate-500 leading-snug">{f}</li>
                ))}
            </ul>
        )}
    </figure>
);
